/**
 * `heysegment recall` — pull workspace knowledge as markdown.
 *
 *   heysegment recall            → the catalog: what can I recall, and is it populated?
 *   heysegment recall <topic>    → that topic rendered as markdown (e.g. business, images)
 *
 * The workspace is never named on the wire — the server derives it from the
 * authenticated session. The body of every answer is markdown for an agent to
 * ingest, printed straight to stdout.
 *
 * @module
 */
import { Console, Effect, Option } from "effect"
import { Argument, Command } from "effect/unstable/cli"
import type { RecallTopicInfo } from "@repo/contracts/rpc/procedures/session"
import { withSession, step } from "../session/with-session"
import { SessionClient } from "../rpc"
import { apiUrlFlag, dirFlag } from "./flags"

const topicArg = Argument.string("topic").pipe(
  Argument.withDescription(
    "What to recall (e.g. business, images). Omit to list everything available.",
  ),
  Argument.optional,
)

/** Render the catalog of topics as a markdown menu for the agent. */
const renderCatalog = (topics: ReadonlyArray<RecallTopicInfo>): string => {
  const lines: Array<string> = [
    "# What you can recall about this workspace",
    "",
    "Run `heysegment recall <topic>` to pull any of these as markdown.",
    "",
  ]
  for (const t of topics) {
    const status = t.available ? t.hint : `unavailable — ${t.hint}`
    lines.push(`- **${t.key}** — ${t.summary} _(${status})_`)
  }
  return lines.join("\n")
}

export const recallCommand = Command.make(
  "recall",
  { topic: topicArg, dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      Effect.gen(function* () {
        const client = yield* SessionClient

        if (Option.isNone(flags.topic)) {
          const { topics } = yield* step("recall:list", client.listRecall(void 0))
          yield* Console.log(renderCatalog(topics))
          return
        }

        const doc = yield* step(
          "recall:get",
          client.getRecall({ topic: flags.topic.value }),
        )
        yield* Console.log(doc.markdown)
      }),
    ),
).pipe(
  Command.withDescription(
    "Recall business knowledge and images for the workspace as markdown",
  ),
)
